import { HttpErrorResponse } from '@angular/common/http';
import { createFeatureSelector, createSelector } from '@ngrx/store';

import { AttachmentGroup, RoutesPath, Status } from '@features/succession/enums';
import {
  CustomerInformation,
  DeceasedMoreInformation,
  DocumentAttachmentGroup,
  DocumentItem,
  ListBoxItems,
  TypeDocument,
} from '@features/succession/models';

import {
  CustomerInformationState,
  successionFeature,
} from '../reducers/customer-information.reducer';

export const selectCustomerInformationState = createFeatureSelector<CustomerInformationState>(
  successionFeature.name,
);

export const selectStepNumber = createSelector(
  selectCustomerInformationState,
  (state: CustomerInformationState): number => state?.loadPage,
);

export const selectCustomerInformation = createSelector(
  selectCustomerInformationState,
  (state: CustomerInformationState): CustomerInformation | undefined => state?.customerInformation,
);

export const selectShowSpouse = createSelector(
  selectCustomerInformation,
  (customerInformation?: CustomerInformation): boolean =>
    !!customerInformation?.inheritanceInformations?.showSpouse,
);

export const selectDeceasedInformation = createSelector(
  selectCustomerInformation,
  (customerInformation: CustomerInformation | undefined) =>
    customerInformation?.deceasedInformations,
);

export const selectReporterInformation = createSelector(
  selectCustomerInformation,
  (customerInformation: CustomerInformation | undefined) =>
    customerInformation?.reporterInformations,
);

export const selectNotaryInformation = createSelector(
  selectCustomerInformation,
  (customerInformation: CustomerInformation | undefined) => customerInformation?.notaryInformations,
);

export const selectInheritanceInformation = createSelector(
  selectCustomerInformation,
  (customerInformation: CustomerInformation | undefined) =>
    customerInformation?.inheritanceInformations,
);

export const selectToken = createSelector(
  selectCustomerInformation,
  (customerInformation?: CustomerInformation) => customerInformation?.token,
);

export const selectDeceasedMoreInformation = createSelector(
  selectCustomerInformation,
  (customerInformation?: CustomerInformation) => customerInformation?.deceasedMoreInformations,
);

export const getDocumentsByAttachmentGroup = (
  attachmentGroup: AttachmentGroup,
  documents?: DocumentItem[],
): DocumentItem[] =>
  documents?.filter((document) => document.uploadedAttachmentGroup === attachmentGroup) || [];

export const selectDeceasedMoreInformationFormValues = createSelector(
  selectDeceasedMoreInformation,
  (deceasedMoreInformation?: DeceasedMoreInformation) => {
    const documents: DocumentItem[] | undefined = deceasedMoreInformation?.documents;
    return {
      deceasedDate: deceasedMoreInformation?.deceasedDate
        ? deceasedMoreInformation?.deceasedDate
        : null,
      hasFuneralContract: deceasedMoreInformation?.hasFuneralContract
        ? deceasedMoreInformation?.hasFuneralContract
        : null,
      deathCertificateDocuments: getDocumentsByAttachmentGroup(
        AttachmentGroup.DEATH_CERTIFICATE,
        documents,
      ),
      otherDocuments: getDocumentsByAttachmentGroup(AttachmentGroup.OTHERS, documents),
      funeralHomeBillsDocuments: getDocumentsByAttachmentGroup(
        AttachmentGroup.FUNERAL_HOME_BILLS,
        documents,
      ),
      ribDocuments: getDocumentsByAttachmentGroup(AttachmentGroup.RIB, documents),
      funeralHomePaymentAuthorizationDocuments: getDocumentsByAttachmentGroup(
        AttachmentGroup.FUNERAL_HOME_PAYMENT_AUTHORIZATION,
        documents,
      ),
    };
  },
);

export const selectListBoxItems = createSelector(
  selectCustomerInformationState,
  (state: CustomerInformationState) => state?.listBoxItems,
);

export const selectCustomerInformationLoadStatus = createSelector(
  selectCustomerInformationState,
  (state: CustomerInformationState) => {
    let status = Status.INITIAL;
    if (state.loadingStatus.isLoaded) {
      status = Status.SUCCESS;
    } else if (!!state.loadingStatus.error) {
      status = Status.FAIL;
    }
    return status;
  },
);

export const selectCustomerInformationIsLoading = createSelector(
  selectCustomerInformationState,
  (state: CustomerInformationState) => state.loadingStatus.isLoading,
);

export const selectLoadPage = createSelector(
  selectCustomerInformationState,
  (state: CustomerInformationState): number => state?.loadPage,
);

export const selectLoadPageUrl = createSelector(selectLoadPage, (page: number): string => {
  const pages: Record<number, string> = {
    1: RoutesPath.PAGE_1,
    2: RoutesPath.PAGE_2,
    3: RoutesPath.PAGE_3,
    4: RoutesPath.PAGE_4,
    5: RoutesPath.PAGE_5,
    6: RoutesPath.PAGE_6,
    7: RoutesPath.PAGE_7,
  };
  return pages[page] ?? undefined;
});

export const selectDocumentAttachmentGroups = createSelector(
  selectListBoxItems,
  (listBoxItems?: ListBoxItems) => listBoxItems?.documentAttachmentGroups,
);

export const selectSelfCareTypeDocuments = createSelector(
  selectListBoxItems,
  (listBoxItems?: ListBoxItems) => listBoxItems?.typeDocuments,
);

const selectAttachmentGroup = (
  attachmentGroup: AttachmentGroup,
  documentAttachmentGroups?: DocumentAttachmentGroup[],
): DocumentAttachmentGroup | undefined =>
  documentAttachmentGroups?.find(
    (documentAttachmentGroup) => documentAttachmentGroup.attachmentGroup === attachmentGroup,
  );

const selectTypeDocuments = (
  documentAttachmentGroup?: DocumentAttachmentGroup,
  typeDocuments?: TypeDocument[],
): TypeDocument[] =>
  typeDocuments?.filter(
    (selfCareTypeDocument) =>
      selfCareTypeDocument.attachmentGroup === documentAttachmentGroup?.attachmentGroup,
  ) || [];

export const selectDeathCertificateAttachmentGroup = createSelector(
  selectDocumentAttachmentGroups,
  (documentAttachmentGroups?: DocumentAttachmentGroup[]) =>
    selectAttachmentGroup(AttachmentGroup.DEATH_CERTIFICATE, documentAttachmentGroups),
);

export const selectDeathCertificateTypeDocuments = createSelector(
  selectSelfCareTypeDocuments,
  selectDeathCertificateAttachmentGroup,
  (typeDocuments?: TypeDocument[], documentAttachmentGroup?: DocumentAttachmentGroup) =>
    selectTypeDocuments(documentAttachmentGroup, typeDocuments),
);

export const selectOtherAttachmentGroup = createSelector(
  selectDocumentAttachmentGroups,
  (documentAttachmentGroups?: DocumentAttachmentGroup[]) =>
    selectAttachmentGroup(AttachmentGroup.OTHERS, documentAttachmentGroups),
);

export const selectOtherSelfCareTypeDocuments = createSelector(
  selectSelfCareTypeDocuments,
  selectOtherAttachmentGroup,
  (typeDocuments?: TypeDocument[], documentAttachmentGroup?: DocumentAttachmentGroup) =>
    selectTypeDocuments(documentAttachmentGroup, typeDocuments),
);

export const selectFuneralHomeBillsAttachmentGroup = createSelector(
  selectDocumentAttachmentGroups,
  (documentAttachmentGroups?: DocumentAttachmentGroup[]) =>
    selectAttachmentGroup(AttachmentGroup.FUNERAL_HOME_BILLS, documentAttachmentGroups),
);

export const selectFuneralHomeBillsTypeDocuments = createSelector(
  selectSelfCareTypeDocuments,
  selectFuneralHomeBillsAttachmentGroup,
  (typeDocuments?: TypeDocument[], documentAttachmentGroup?: DocumentAttachmentGroup) =>
    selectTypeDocuments(documentAttachmentGroup, typeDocuments),
);

export const selectRibAttachmentGroup = createSelector(
  selectDocumentAttachmentGroups,
  (documentAttachmentGroups?: DocumentAttachmentGroup[]) =>
    selectAttachmentGroup(AttachmentGroup.RIB, documentAttachmentGroups),
);

export const selectRibTypeDocuments = createSelector(
  selectSelfCareTypeDocuments,
  selectRibAttachmentGroup,
  (typeDocuments?: TypeDocument[], documentAttachmentGroup?: DocumentAttachmentGroup) =>
    selectTypeDocuments(documentAttachmentGroup, typeDocuments),
);

export const selectFuneralHomePaymentAuthorizationAttachmentGroup = createSelector(
  selectDocumentAttachmentGroups,
  (documentAttachmentGroups?: DocumentAttachmentGroup[]) =>
    selectAttachmentGroup(
      AttachmentGroup.FUNERAL_HOME_PAYMENT_AUTHORIZATION,
      documentAttachmentGroups,
    ),
);

export const selectFuneralHomePaymentAuthorizationTypeDocuments = createSelector(
  selectSelfCareTypeDocuments,
  selectFuneralHomePaymentAuthorizationAttachmentGroup,
  (typeDocuments?: TypeDocument[], documentAttachmentGroup?: DocumentAttachmentGroup) =>
    selectTypeDocuments(documentAttachmentGroup, typeDocuments),
);

export const selectCustomerInformationErrorCode = createSelector(
  selectCustomerInformationState,
  (state: CustomerInformationState) => {
    if (state?.loadingStatus.error instanceof HttpErrorResponse) {
      return state?.loadingStatus.error.status.toString();
    }
    if (state?.loadingStatus.error?.isFormOK) {
      return state?.loadingStatus.error?.isFormOK;
    }
    return undefined;
  },
);

export const selectLastCustomerInformationRequest = createSelector(
  selectCustomerInformationState,
  (state: CustomerInformationState): CustomerInformation | undefined => state.lastRequest,
);
